import * as intlDisplayNames from '@formatjs/intl-displaynames/should-polyfill';
import * as intlGetCanonicalLocales from '@formatjs/intl-getcanonicallocales/should-polyfill';
import * as intlLocale from '@formatjs/intl-locale/should-polyfill';
import * as intlPluralRules from '@formatjs/intl-pluralrules/should-polyfill';
import * as intlRelativeTimeFormat from '@formatjs/intl-relativetimeformat/should-polyfill';
import { ReactElement, Suspense } from 'react';

const needsPolyfills = () => {
  try {
    return (
      intlPluralRules.shouldPolyfill() ||
      intlRelativeTimeFormat.shouldPolyfill() ||
      intlDisplayNames.shouldPolyfill()
    );
  } catch {
    // chances are that if something went wrong we need polyfills
    return true;
  }
};

const loadPolyfills = async () => {
  // these are prerequisites for all the other polyfills
  await Promise.all(
    [
      intlLocale.shouldPolyfill() && import('@formatjs/intl-locale/polyfill'),
      intlGetCanonicalLocales.shouldPolyfill() &&
        import('@formatjs/intl-getcanonicallocales/polyfill'),
    ].filter(Boolean),
  );

  await Promise.all(
    [
      intlPluralRules.shouldPolyfill() &&
        import(/* webpackChunkName: "intl-polyfill-plural-rules" */ './intl-pluralrules'),
      intlRelativeTimeFormat.shouldPolyfill() &&
        import(
          /* webpackChunkName: "intl-polyfill-relative-time-format" */ './intl-relativetimeformat'
        ),
      intlDisplayNames.shouldPolyfill() &&
        import(/* webpackChunkName: "intl-polyfill-displaynames" */ './intl-displaynames'),
    ].filter(Boolean),
  );
};

// the LoaderCheck fakes being a Lazy component by throwing a promise, which Suspense handles
let fakeSuspense = !needsPolyfills();
const LoaderCheck: FC = ({ children }) => {
  if (!fakeSuspense) {
    throw loadPolyfills().then(() => (fakeSuspense = true));
  }

  // no point wrapping this in a Fragment. Shut up TypeScript!
  return children as any;
};

const PolyfillLoader: FC<{ fallback?: ReactElement }> = ({ fallback, children }) => (
  <Suspense fallback={fallback || null}>
    <LoaderCheck>{children}</LoaderCheck>
  </Suspense>
);

export default PolyfillLoader;
